import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Trash2, Plus, Save, Search, UploadCloud, Star, X } from 'lucide-react';
import { uploadProductImage } from '../../utils/imageUpload';

var API = process.env.REACT_APP_BACKEND_URL;

function headers() {
  var t = localStorage.getItem('admin_token');
  return t ? { Authorization: 'Bearer ' + t } : {};
}

function jsonHeaders() {
  return Object.assign({ 'Content-Type': 'application/json' }, headers());
}

var KEY_OPTIONS = [
  { value: '', label: 'Auto (from title)' },
  { value: 'c', label: 'C (concert)' },
  { value: 'bb', label: 'Bb' },
  { value: 'eb', label: 'Eb' },
  { value: 'bass', label: 'Bass clef' },
  { value: 'none', label: 'No key / solo book' },
];

var PLATFORM_OPTIONS = [
  { value: '', label: '— not a book —' },
  { value: 'forscore', label: 'forScore' },
  { value: 'mobilesheets', label: 'MobileSheets' },
];

var CATEGORY_OPTIONS = [
  { value: 'sheet-music', label: 'Sheet Music & Digital' },
  { value: 'accessories', label: 'Accessories' },
  { value: 'mutes', label: 'Mutes & Practice' },
  { value: 'instruments', label: 'Instruments' },
];

var INSTRUMENT_OPTIONS = [
  { value: '', label: 'Auto (from key)' },
  { value: 'trumpet', label: 'Trumpet / French Horn' },
  { value: 'saxophone', label: 'Saxophone / Clarinet' },
  { value: 'trombone', label: 'Trombone / Bass' },
  { value: 'piano', label: 'Piano / Guitar' },
];

var TYPE_OPTIONS = [
  { value: 'digital', label: 'Digital download' },
  { value: 'physical', label: 'Physical product' },
];

function Field(props) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">{props.label}</span>
      {props.children}
    </label>
  );
}

var inputClass = 'w-full bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#d4af37] outline-none';

function ProductEditor(props) {
  var product = props.product;
  var onSaved = props.onSaved;
  var onDeleted = props.onDeleted;
  var [form, setForm] = useState({
    name: product.name || '',
    price: product.price != null ? String(product.price) : '',
    platform: product.platform || '',
    bookKey: product.bookKey || '',
    instrument: product.instrument || '',
    type: product.isDigital ? 'digital' : 'physical',
    category: product.category || 'sheet-music',
    youtubeUrl: product.youtubeUrl || '',
    description: product.description || '',
  });
  var [images, setImages] = useState(product.images || []);
  var [primary, setPrimary] = useState(product.image || '');
  var [newImageUrl, setNewImageUrl] = useState('');
  var [busy, setBusy] = useState('');
  var [msg, setMsg] = useState(null);
  var fileRef = useRef(null);

  useEffect(function() {
    setForm({
      name: product.name || '',
      price: product.price != null ? String(product.price) : '',
      platform: product.platform || '',
      bookKey: product.bookKey || '',
      instrument: product.instrument || '',
      type: product.isDigital ? 'digital' : 'physical',
      category: product.category || 'sheet-music',
      youtubeUrl: product.youtubeUrl || '',
      description: product.description || '',
    });
    setImages(product.images || []);
    setPrimary(product.image || '');
    setMsg(null);
  }, [product.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  function set(key, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [key]: value }); });
  }

  async function saveDetails() {
    setBusy('save'); setMsg(null);
    try {
      var body = {
        name: form.name,
        platform: form.platform,
        bookKey: form.bookKey,
        instrument: form.instrument,
        category: form.category,
        isDigital: form.type === 'digital',
        youtubeUrl: form.youtubeUrl,
        description: form.description,
      };
      if (form.price !== '') body.price = Number(form.price);
      var r = await fetch(API + '/api/admin/catalog/' + encodeURIComponent(product.slug), {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(body),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setMsg({ type: 'ok', text: 'Saved' });
      onSaved();
    } catch (e) {
      setMsg({ type: 'err', text: e.message || String(e) });
    } finally { setBusy(''); }
  }

  async function saveImages(nextImages, nextPrimary) {
    setBusy('images'); setMsg(null);
    try {
      var r = await fetch(API + '/api/admin/product-images/' + encodeURIComponent(product.slug) + '/reorder', {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ images: nextImages, image: nextPrimary || (nextImages[0] || null) }),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setImages(d.images || []);
      setPrimary(d.image || '');
      onSaved();
    } catch (e) {
      setMsg({ type: 'err', text: e.message || String(e) });
    } finally { setBusy(''); }
  }

  async function uploadImage(file) {
    if (!file) return;
    setBusy('upload'); setMsg(null);
    try {
      var d = await uploadProductImage(product.slug, file, images.length === 0);
      setImages(d.images || []);
      setPrimary(d.image || '');
      setMsg({ type: 'ok', text: 'Uploaded to Dropbox' });
      onSaved();
    } catch (e) {
      setMsg({ type: 'err', text: e.message || String(e) });
    } finally {
      setBusy('');
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeProduct() {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Permanently delete "' + product.name + '"? It will disappear from the shop and the admin catalog.')) return;
    setBusy('delete');
    try {
      var r = await fetch(API + '/api/admin/catalog/' + encodeURIComponent(product.slug), {
        method: 'DELETE', headers: headers(),
      });
      if (!r.ok) {
        var d = await r.json().catch(function() { return {}; });
        throw new Error(d.detail || 'Delete failed');
      }
      onDeleted();
    } catch (e) {
      setMsg({ type: 'err', text: e.message || String(e) });
    } finally { setBusy(''); }
  }

  return (
    <div className="border border-white/10 rounded-xl p-4 bg-neutral-950/60" data-testid="catalog-editor">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-white font-bold text-base">{product.name}</h3>
          <p className="text-xs text-neutral-500 font-mono">{product.slug}</p>
        </div>
        <button
          onClick={removeProduct}
          disabled={busy === 'delete'}
          data-testid="catalog-delete-btn"
          className="shrink-0 text-xs bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20 px-3 py-1.5 rounded-lg disabled:opacity-50"
        >
          <Trash2 className="w-3.5 h-3.5 inline mr-1" />Delete product
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <Field label="Name">
          <input data-testid="catalog-name-input" className={inputClass} value={form.name} onChange={function(e) { set('name', e.target.value); }} />
        </Field>
        <Field label="Price (USD)">
          <input data-testid="catalog-price-input" className={inputClass} value={form.price} onChange={function(e) { set('price', e.target.value); }} />
        </Field>
        <Field label="App / platform">
          <select data-testid="catalog-platform-select" className={inputClass} value={form.platform} onChange={function(e) { set('platform', e.target.value); }}>
            {PLATFORM_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Key">
          <select data-testid="catalog-key-select" className={inputClass} value={form.bookKey} onChange={function(e) { set('bookKey', e.target.value); }}>
            {KEY_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Category (shop filter)">
          <select data-testid="catalog-category-select" className={inputClass} value={form.category} onChange={function(e) { set('category', e.target.value); }}>
            {CATEGORY_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Type">
          <select data-testid="catalog-type-select" className={inputClass} value={form.type} onChange={function(e) { set('type', e.target.value); }}>
            {TYPE_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Instrument group">
          <select data-testid="catalog-instrument-select" className={inputClass} value={form.instrument} onChange={function(e) { set('instrument', e.target.value); }}>
            {INSTRUMENT_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Video (YouTube URL)">
          <input data-testid="catalog-video-input" className={inputClass} value={form.youtubeUrl} onChange={function(e) { set('youtubeUrl', e.target.value); }} />
        </Field>
      </div>

      <Field label="Description">
        <textarea data-testid="catalog-description-input" rows={4} className={inputClass} value={form.description} onChange={function(e) { set('description', e.target.value); }} />
      </Field>

      <div className="flex items-center gap-3 mt-3">
        <button
          onClick={saveDetails}
          disabled={busy === 'save'}
          data-testid="catalog-save-btn"
          className="bg-[#d4af37] text-black font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-50"
        >
          {busy === 'save' ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Save className="w-4 h-4 inline mr-1" />}
          Save changes
        </button>
        {msg && (
          <span data-testid="catalog-editor-msg" className={'text-xs ' + (msg.type === 'ok' ? 'text-emerald-400' : 'text-red-400')}>{msg.text}</span>
        )}
      </div>

      {/* Images */}
      <div className="mt-6 pt-4 border-t border-white/10">
        <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Images ({images.length})</p>
        <div className="flex flex-wrap gap-3 mb-3">
          {images.map(function(url, idx) {
            return (
              <div key={url + idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-white/10 bg-white">
                <img src={url} alt="" className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                {url === primary && (
                  <span className="absolute top-1 left-1 bg-[#d4af37] text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                    <Star className="w-2.5 h-2.5 inline" /> MAIN
                  </span>
                )}
                <div className="absolute bottom-1 right-1 flex gap-1">
                  {url !== primary && (
                    <button
                      onClick={function() { saveImages(images, url); }}
                      data-testid={'catalog-set-primary-' + idx}
                      title="Set as main image"
                      className="bg-black/70 text-[#d4af37] rounded p-1"
                    ><Star className="w-3 h-3" /></button>
                  )}
                  <button
                    onClick={function() { saveImages(images.filter(function(_, i) { return i !== idx; }), url === primary ? '' : primary); }}
                    data-testid={'catalog-remove-image-' + idx}
                    title="Remove image"
                    className="bg-black/70 text-red-400 rounded p-1"
                  ><X className="w-3 h-3" /></button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="catalog-image-file"
            onChange={function(e) { uploadImage(e.target.files && e.target.files[0]); }}
            className="text-xs text-neutral-400"
          />
          <span className="text-xs text-neutral-600">uploads straight to your Dropbox</span>
          {busy === 'upload' && <Loader2 className="w-4 h-4 animate-spin text-[#d4af37]" />}
        </div>
        <div className="flex gap-2 mt-3">
          <input
            placeholder="…or paste an image URL"
            value={newImageUrl}
            data-testid="catalog-image-url-input"
            onChange={function(e) { setNewImageUrl(e.target.value); }}
            className={inputClass}
          />
          <button
            onClick={function() { if (newImageUrl.trim()) { saveImages(images.concat([newImageUrl.trim()]), primary); setNewImageUrl(''); } }}
            data-testid="catalog-add-image-url-btn"
            className="shrink-0 bg-neutral-800 text-white text-sm px-4 rounded-lg"
          ><UploadCloud className="w-4 h-4 inline mr-1" />Add</button>
        </div>
      </div>
    </div>
  );
}

function CreateProductForm(props) {
  var onCreated = props.onCreated;
  var [open, setOpen] = useState(false);
  var [form, setForm] = useState({
    name: '', price: '9.99', platform: 'forscore', bookKey: '', category: 'sheet-music',
    description: '', youtubeUrl: '', image: '', dropbox_path: '',
  });
  var [busy, setBusy] = useState(false);
  var [msg, setMsg] = useState(null);

  function set(k, v) { setForm(function(p) { return Object.assign({}, p, { [k]: v }); }); }

  async function create() {
    setBusy(true); setMsg(null);
    try {
      var body = {
        name: form.name,
        price: Number(form.price || 0),
        platform: form.platform,
        bookKey: form.bookKey,
        category: form.category,
        description: form.description,
        youtubeUrl: form.youtubeUrl,
        images: form.image ? [form.image] : [],
        image: form.image || null,
        dropbox_path: form.dropbox_path || null,
        isDigital: true,
        hidden: true,
      };
      var r = await fetch(API + '/api/admin/catalog', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Create failed');
      setMsg({ type: 'ok', text: 'Created ' + d.slug + ' — it starts hidden, use "Show in shop" when ready' });
      setForm({ name: '', price: '9.99', platform: 'forscore', bookKey: '', category: 'sheet-music', description: '', youtubeUrl: '', image: '', dropbox_path: '' });
      onCreated();
    } catch (e) {
      setMsg({ type: 'err', text: e.message || String(e) });
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <button
        onClick={function() { setOpen(true); }}
        data-testid="catalog-new-product-btn"
        className="mb-4 bg-[#d4af37] text-black font-semibold text-sm px-4 py-2 rounded-lg"
      ><Plus className="w-4 h-4 inline mr-1" />New product</button>
    );
  }

  return (
    <div className="mb-6 border border-[#d4af37]/40 rounded-xl p-4 bg-neutral-950" data-testid="catalog-new-product-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold">New product</h3>
        <button onClick={function() { setOpen(false); }} className="text-neutral-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Name *"><input data-testid="new-name-input" className={inputClass} value={form.name} onChange={function(e) { set('name', e.target.value); }} /></Field>
        <Field label="Price (USD)"><input data-testid="new-price-input" className={inputClass} value={form.price} onChange={function(e) { set('price', e.target.value); }} /></Field>
        <Field label="App / platform">
          <select data-testid="new-platform-select" className={inputClass} value={form.platform} onChange={function(e) { set('platform', e.target.value); }}>
            {PLATFORM_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Key">
          <select data-testid="new-key-select" className={inputClass} value={form.bookKey} onChange={function(e) { set('bookKey', e.target.value); }}>
            {KEY_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Category">
          <select data-testid="new-category-select" className={inputClass} value={form.category} onChange={function(e) { set('category', e.target.value); }}>
            {CATEGORY_OPTIONS.map(function(o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
          </select>
        </Field>
        <Field label="Video (YouTube URL)"><input data-testid="new-video-input" className={inputClass} value={form.youtubeUrl} onChange={function(e) { set('youtubeUrl', e.target.value); }} /></Field>
        <Field label="Cover image URL"><input data-testid="new-image-input" className={inputClass} value={form.image} onChange={function(e) { set('image', e.target.value); }} /></Field>
        <Field label="Dropbox file path (delivered after purchase)">
          <input data-testid="new-dropbox-path-input" className={inputClass} placeholder="/Books/Real Book Vol 4 in C.4sc" value={form.dropbox_path} onChange={function(e) { set('dropbox_path', e.target.value); }} />
        </Field>
      </div>
      <Field label="Description">
        <textarea rows={3} data-testid="new-description-input" className={inputClass} value={form.description} onChange={function(e) { set('description', e.target.value); }} />
      </Field>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={create} disabled={busy || !form.name.trim()} data-testid="catalog-create-btn" className="bg-[#d4af37] text-black font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 inline animate-spin mr-1" /> : <Plus className="w-4 h-4 inline mr-1" />}Create product
        </button>
        {msg && <span data-testid="catalog-create-msg" className={'text-xs ' + (msg.type === 'ok' ? 'text-emerald-400' : 'text-red-400')}>{msg.text}</span>}
      </div>
    </div>
  );
}

export default function CatalogPanel() {
  var [products, setProducts] = useState([]);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState('');
  var [query, setQuery] = useState('');
  var [filter, setFilter] = useState('all');
  var [selected, setSelected] = useState('');
  var [visBusy, setVisBusy] = useState('');

  var load = useCallback(async function() {
    setLoading(true); setErr('');
    try {
      var r = await fetch(API + '/api/admin/catalog', { headers: headers() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      var d = await r.json();
      setProducts(d.products || []);
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(function() { load(); }, [load]);

  async function toggleHidden(slug, hidden) {
    setVisBusy(slug); setErr('');
    try {
      var r = await fetch(API + '/api/admin/product-visibility/' + encodeURIComponent(slug), {
        method: 'PUT', headers: jsonHeaders(), body: JSON.stringify({ hidden: hidden }),
      });
      // Never swallow this: a silent 401 made the toggle look like it did nothing.
      if (!r.ok) {
        var d = await r.json().catch(function() { return {}; });
        throw new Error(r.status === 401
          ? 'Your admin session expired — log out and log in again, then try once more.'
          : (d.detail || 'Could not change visibility (HTTP ' + r.status + ')'));
      }
      await load();
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setVisBusy(''); }
  }

  var q = query.trim().toLowerCase();
  var rows = products.filter(function(p) {
    if (filter === 'forscore' && p.platform !== 'forscore') return false;
    if (filter === 'mobilesheets' && p.platform !== 'mobilesheets') return false;
    if (filter === 'hidden' && !p.hidden) return false;
    if (filter === 'edited' && !(p.edited_fields || []).length && !p.admin_created) return false;
    if (!q) return true;
    return ((p.name || '') + ' ' + (p.slug || '')).toLowerCase().indexOf(q) !== -1;
  });
  var selectedProduct = products.find(function(p) { return p.slug === selected; });

  return (
    <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6" data-testid="catalog-panel">
      <h2 className="text-xl font-bold text-white mb-1">Catalog</h2>
      <p className="text-neutral-500 text-sm mb-5">
        Rename, reprice, change the key, move a book between forScore and MobileSheets, swap images or video, delete a product, or create a new one. Every change goes live instantly — no redeploy.
      </p>

      <CreateProductForm onCreated={load} />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[
          { id: 'all', label: 'All' },
          { id: 'forscore', label: 'forScore' },
          { id: 'mobilesheets', label: 'MobileSheets' },
          { id: 'hidden', label: 'Hidden' },
          { id: 'edited', label: 'Edited' },
        ].map(function(f) {
          return (
            <button
              key={f.id}
              onClick={function() { setFilter(f.id); }}
              data-testid={'catalog-filter-' + f.id}
              className={'px-3 py-1.5 rounded-full text-xs font-semibold ' + (filter === f.id ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-300')}
            >{f.label}</button>
          );
        })}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-neutral-600" />
          <input
            placeholder="Search products…"
            value={query}
            data-testid="catalog-search-input"
            onChange={function(e) { setQuery(e.target.value); }}
            className="bg-neutral-950 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#d4af37]"
          />
        </div>
      </div>

      {err && <p className="text-red-400 text-sm mb-3" data-testid="catalog-error">{err}</p>}
      {loading && <Loader2 className="w-5 h-5 animate-spin text-[#d4af37]" />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="max-h-[560px] overflow-y-auto border border-white/10 rounded-xl divide-y divide-white/5" data-testid="catalog-list">
          {rows.map(function(p) {
            return (
              <div
                key={p.slug}
                className={'flex items-center gap-3 px-3 py-2.5 cursor-pointer ' + (selected === p.slug ? 'bg-[#d4af37]/10' : 'hover:bg-white/5')}
                onClick={function() { setSelected(p.slug); }}
                data-testid={'catalog-row-' + p.slug}
              >
                <div className="w-10 h-10 shrink-0 rounded bg-white overflow-hidden">
                  {p.image && <img src={p.image} alt="" className="w-full h-full object-contain" referrerPolicy="no-referrer" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={'text-sm font-medium truncate ' + (p.platform === 'mobilesheets' ? 'text-emerald-400' : p.platform === 'forscore' ? 'text-sky-400' : 'text-white')}>
                    {p.name}
                  </p>
                  <p className="text-[11px] text-neutral-600 truncate font-mono">{p.slug} · ${p.price}</p>
                </div>
                {(p.edited_fields || []).length > 0 && <span className="text-[10px] text-[#d4af37]">edited</span>}
                <button
                  onClick={function(e) { e.stopPropagation(); toggleHidden(p.slug, !p.hidden); }}
                  disabled={visBusy === p.slug}
                  data-testid={'catalog-toggle-visibility-' + p.slug}
                  className={'shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full ' + (p.hidden ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-300')}
                >{p.hidden ? 'Show in shop' : 'Hide'}</button>
              </div>
            );
          })}
          {!loading && rows.length === 0 && <p className="p-6 text-center text-neutral-600 text-sm">No products match.</p>}
        </div>

        <div>
          {selectedProduct ? (
            <ProductEditor
              product={selectedProduct}
              onSaved={load}
              onDeleted={function() { setSelected(''); load(); }}
            />
          ) : (
            <p className="text-neutral-600 text-sm border border-dashed border-white/10 rounded-xl p-8 text-center">
              Pick a product on the left to edit it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
